import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * ODV Timezone Utilities
 *
 * The tickets table stores created_at in UTC. When querying by local date,
 * we need to convert local date boundaries to UTC for accurate queries.
 *
 * Example: Nov 27 in Eastern time
 * - Nov 27 00:00:00 EST = Nov 27 05:00:00 UTC
 * - Nov 27 23:59:59 EST = Nov 28 04:59:59 UTC
 */

// ODV operates in Eastern timezone
export const ODV_TIMEZONE = 'America/New_York';

const easternWallClockFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: ODV_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const easternParts = (instant) => Object.fromEntries(
  easternWallClockFormatter
    .formatToParts(instant)
    .filter((part) => part.type !== 'literal')
    .map((part) => [part.type, Number(part.value)]),
);

/**
 * Interpret a datetime-local value as an ODV Eastern wall-clock time.
 * This is deliberately independent of the browser or operator timezone and
 * rejects nonexistent DST wall times instead of silently shifting them.
 */
export function easternWallTimeToUTC(value) {
  const match = String(value || '').match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (!match) return null;

  const [, year, month, day, hour, minute, second = '0'] = match;
  const requested = {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second),
  };
  const requestedAsUtc = Date.UTC(
    requested.year,
    requested.month - 1,
    requested.day,
    requested.hour,
    requested.minute,
    requested.second,
  );
  if (!Number.isFinite(requestedAsUtc)) return null;

  let instantMs = requestedAsUtc;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const observed = easternParts(new Date(instantMs));
    const observedAsUtc = Date.UTC(
      observed.year,
      observed.month - 1,
      observed.day,
      observed.hour,
      observed.minute,
      observed.second,
    );
    instantMs += requestedAsUtc - observedAsUtc;
  }

  const finalParts = easternParts(new Date(instantMs));
  const matchesRequested = Object.entries(requested)
    .every(([key, expected]) => finalParts[key] === expected);
  return matchesRequested ? new Date(instantMs).toISOString() : null;
}

/**
 * Convert a local date string (yyyy-MM-dd) to UTC ISO string for start of day
 * Example: '2024-11-27' in Eastern → '2024-11-27T05:00:00.000Z' (EST) or T04:00 (EDT)
 */
export function localDateToUTCStart(dateStr) {
  // Parse as local date at midnight
  const [year, month, day] = dateStr.split('-').map(Number);
  const localDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  return localDate.toISOString();
}

/**
 * Convert a local date string (yyyy-MM-dd) to UTC ISO string for end of day
 * Example: '2024-11-27' in Eastern → '2024-11-28T04:59:59.999Z' (EST) or T03:59 (EDT)
 */
export function localDateToUTCEnd(dateStr) {
  // Parse as local date at end of day
  const [year, month, day] = dateStr.split('-').map(Number);
  const localDate = new Date(year, month - 1, day, 23, 59, 59, 999);
  return localDate.toISOString();
}

/**
 * Convert a Date object representing local time to UTC ISO string
 * Preserves the local time interpretation when converting to UTC
 */
export function localDateTimeToUTC(date) {
  return date.toISOString();
}

/**
 * Get local date string (yyyy-MM-dd) from a UTC timestamp
 * Converts UTC to local timezone before extracting date
 */
export function utcToLocalDateString(utcTimestamp) {
  const date = new Date(utcTimestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a UTC timestamp as local date for display grouping
 * This is critical for grouping tickets by local business day
 */
export function getLocalDateFromUTC(utcTimestamp) {
  return new Date(utcTimestamp);
}
